import { Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { regionIdState, storeIdState } from '../../../Store/atoms';
import { filterVisibleProducts } from '../../../Menu/utils/filterVisibleProducts';
import { dedupe } from '../../../utils/dedupe';
import { sortAlphabetical } from '../../../utils/sortAlphabetical';
import { getIsProductGroupVisible } from '../../../utils/getIsProductGroupVisible';
import { useSearchNamesQuery } from '../../../Header/graphql';
import { Loading } from '../../../components/Loading';
import { CircleImage } from '../../../components/CircleImage';

type Name = { id: string; name: string; icon: string };

export type HeaderSearchProps = React.HTMLAttributes<HTMLDivElement>;

export const SearchList: React.FC<HeaderSearchProps> = () => {
	const navigate = useNavigate();
	const url =
		window.location.href.split('/')[4] !== undefined
			? window.location.href.split('/')[4].replace(/%20/g, '')
			: '';
	const [names, setNames] = useState<Name[]>([]);
	const [value, setValue] = useState(url);
	const [active] = useState(true);
	const { data } = useSearchNamesQuery();
	const storeId = useRecoilValue(storeIdState);
	const regionId = useRecoilValue(regionIdState);
	const filter = useMemo(
		() => filterVisibleProducts(storeId, regionId),
		[storeId, regionId]
	);
	const results = useMemo(
		() =>
			value !== '' && active
				? names
						.filter(({ name }) =>
							name.toLowerCase().includes(value.toLowerCase())
						)
						.filter((_, i) => i <= 100)
				: [],
		[active, names, value]
	);

	if (value !== url) {
		setValue(url);
	}

	const isProductGroupVisible = useMemo(
		() => getIsProductGroupVisible(data?.onlineProductGroups ?? []),
		[data]
	);

	useEffect(() => {
		if (data) {
			setNames(
				dedupe(
					data.onlineProductGroups
						.filter(({ id }) => isProductGroupVisible(Number(id)))
						.flatMap(({ products }) => products)
						.filter(filter)
						.map(({ id, name, icon }) => ({ id, name, icon })),
					(a, b) => a.id === b.id
				).sort((a, b) => sortAlphabetical(a.name, b.name))
			);
		}
	}, [data, filter, isProductGroupVisible]);

	if (!data) {
		return <Loading />;
	}

	return (
		<Suspense fallback={<Loading />}>
			<div className="product-list text-center menu-products-list">
				<div className="row text-center no-gutters">
					{results.map(({ id, name, icon }) => (
						<CircleImage
							className="products-list-product col-6 col-md-4 h-100"
							size="small"
							label={name}
							alt={name}
							key={name}
							url={icon}
							onClick={() => {
								navigate(`/product/${id}`);
							}}
						/>
					))}
					{results.length === 0 ? (
						<h2 className="row no-gutters text-center">
							No Results
						</h2>
					) : null}
				</div>
			</div>
		</Suspense>
	);
};
